// src/hooks/useBusinessId.tsx
import { useState, useEffect } from 'react';
import { useAuthContext } from '@/context/AuthContext';

/**
 * Хук для централизованного получения ID бизнеса
 * @returns {Object} { businessId, isLoading, error }
 */
export const useBusinessId = () => {
  const { user, isAuthenticated, isLoading: isAuthLoading } = useAuthContext();
  const [businessId, setBusinessId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const determineBusinessId = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Ждем завершения загрузки контекста авторизации
        if (isAuthLoading) {
          console.log('useBusinessId: Ожидание загрузки контекста авторизации...');
          return; // Выходим и ждем следующего рендера, когда isAuthLoading изменится
        }

        // Проверяем авторизацию
        if (!isAuthenticated) {
          console.log('useBusinessId: Пользователь не авторизован');
          setError('Пользователь не авторизован');
          setBusinessId(null);
          setIsLoading(false);
          return;
        }

        // Проверяем наличие пользователя
        if (!user) {
          console.log('useBusinessId: Данные пользователя отсутствуют');
          setError('Данные пользователя отсутствуют');
          setBusinessId(null);
          setIsLoading(false);
          return;
        }

        // Проверяем наличие id в данных пользователя
        if (!user.id) {
          console.log('useBusinessId: ID бизнеса отсутствует в данных пользователя');
          setError('ID бизнеса отсутствует в данных пользователя');
          setBusinessId(null);
          setIsLoading(false);
          return;
        }

        // Устанавливаем businessId из данных пользователя
        console.log(`useBusinessId: ID бизнеса получен из контекста авторизации: ${user.id}`);
        setBusinessId(user.id);
        setIsLoading(false);
      } catch (err) {
        console.error('useBusinessId: Ошибка при получении ID бизнеса:', err);
        setError('Ошибка при получении ID бизнеса');
        setIsLoading(false);
      }
    };

    determineBusinessId();
  }, [user, isAuthenticated, isAuthLoading]);

  return { businessId, isLoading, error };
};

export default useBusinessId;